import React, { useState, useRef, useEffect } from 'react';
import { Link, navigate } from 'gatsby';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    AppBar,
    Toolbar,
    IconButton,
    Button,
    ButtonGroup,
    Typography
} from '@mui/material';
import { MenuItem, ContentReference, opciones, logosHeader } from '../../../interfaces/HeaderMobile';
import { styles } from './styles';
import ZonaTransaccional from '../zona-transaccional/zonaTransaccional';

interface MobileMenuProps {
    menuOpciones: MenuItem[];
    referentContent?: ContentReference[];
    active: string | null;
    opciones: opciones[];
    logosHeader: logosHeader[];
    handleClick: (item: MenuItem) => void;
    handleDrawerClose: () => void;
    isDark: boolean;
    logoMagenta: { file: { url: string } };
    logoBlack: { file: { url: string } };
    handleSearcher: () => void;
    renderLoginButton: (gigyaContext: any) => JSX.Element;
    gigyaContext: any;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
    menuOpciones,
    referentContent,
    active,
    opciones,
    logosHeader,
    handleClick,
    handleDrawerClose,
    isDark,
    logoMagenta,
    logoBlack,
}) => {
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
 

    useEffect(() => {
        document.body.style.overflow = 'hidden'; 
        return () => {
            document.body.style.overflow = 'auto'; 
        };
    }, []);
    
    
  
    const constructUrl = (targetPath = '') => {
        const base = window.location.origin;
        const [firstSegment] = window.location.pathname.split('/').filter(Boolean);
        const cleanedPath = targetPath.startsWith('/') ? targetPath.slice(1) : targetPath;
      
        // Evitar duplicar el primer segmento
        const finalPath = cleanedPath.startsWith(firstSegment + '/') 
          ? cleanedPath 
          : `${firstSegment}/${cleanedPath}`;
      
        return `${base}/${finalPath}`;
      };
      

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: 'white',
                    boxShadow: 'none',
                    borderBottom: '1px solid #e0e0e0',
                }}
                role="navigation"
                aria-label="Menú móvil"
            >
                <Toolbar sx={{
                    justifyContent: 'space-between',
                    padding: '0',
                    minHeight: '60px !important',
                    pr: '0 !important'
                }}>
                    {/* Logo */}
                    <Link to="/" style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '16px'
                    }}
                        aria-label="Ir a la página de inicio">
                        <img
                            src={isDark ? logoBlack.file.url : logoMagenta.file.url}
                            alt="Logo Servicio de Empleo Comfama"
                            style={{ height: '30px' }}
                        />
                    </Link>

                    {/* Botón cerrar */}
                    <Button
                        ref={menuButtonRef}
                        onClick={handleDrawerClose}
                        sx={{
                            minWidth: 'unset',
                            width: '104px',
                            height: '60px',
                            p: 0,
                            backgroundColor: '#EE2B7B',
                            borderRadius: '0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            '&:hover': {
                                backgroundColor: '#ee2b7b'
                            }
                        }}
                        aria-label="Cerrar menú"
                    >
                        <Box sx={{
                            fontSize: '24px',
                            fontWeight: 'normal',
                            lineHeight: 1,
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'white',
                        }}>
                            ×
                        </Box>
                        <Box sx={{
                            fontSize: '12px',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 'normal',
                            lineHeight: 1
                        }}>
                            Menú
                        </Box>
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Contenido del menú */}
            <Box
                 tabIndex={-1}
                sx={{ width: '100vw', mt: '60px' }}
                role="region"
                aria-label="Contenido del menú"
            >
                {/* Zona transaccional y Ayuda */}
                <Box sx={{
                    display: 'flex',
                    gap: '24px',
                    padding: '20px 0 18px',
                    justifyContent: 'center',
                    backgroundColor: '#F8F9FA'
                }}>
                    <ZonaTransaccional />
                    <Button
                        href="https://www.comfama.com/ayuda"
                        variant="contained"
                        sx={{
                            backgroundColor: '#EE2B7B',
                            color: 'white',
                            width: '71px',
                            height: '33px',
                            borderRadius: '20px',
                            textTransform: 'none',
                            fontWeight: '200',
                            padding: '6px 12px',
                            '&:hover': { backgroundColor: '#d41c6c' }
                        }}
                        aria-label="Ir a la sección de ayuda"
                    >
                        Ayuda
                    </Button>
                </Box>

                {/* Switch Personas/Empresas */}
                <Box sx={{
                    padding: '41px 0px 12px',
                    display: 'flex',
                    justifyContent: 'center'
                }}
                    role="group"
                    aria-label="Selección de perfil">
                    <ButtonGroup disableElevation sx={styles.buttonGroup}>
                        {menuOpciones.map((item, index) => (
                            <Button
                                key={index}
                                onClick={() => handleClick(item)}
                                sx={styles.menuButton(active === item.nombreTag)}
                                aria-pressed={active === item.nombreTag}
                                aria-label={`Ver contenido para ${item.nombreTag}`}
                            >
                                {item.nombreTag}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>

                {/* Menú principal */}
                <List
                    sx={{
                        padding: '13px',
                        '& > *': {
                            marginBottom: '20px'
                        }
                    }}
                    role="menu"
                    aria-label="Menú principal"
                >
                    {referentContent?.map((item, index) => {
                        const hasSubMenu = Array.isArray(item.referenciaContenido) &&
                            item.referenciaContenido.length > 0;

                        return (
                            <React.Fragment key={index}>
                                <ListItem
                                    component="button"
                                    role="menuitem"
                                    aria-expanded={expandedItem === item.tipoTitulo}
                                    aria-controls={hasSubMenu ? `submenu-${index}` : undefined}
                                    aria-haspopup={hasSubMenu}
                                    onClick={() => {
                                        if (hasSubMenu) {
                                            setExpandedItem(expandedItem === item.tipoTitulo ? null : item.tipoTitulo);
                                        } else if (item.redireccionBoton) {
                                            handleDrawerClose();

                                            const url = item.redireccionBoton.startsWith('/')
                                                ? item.redireccionBoton
                                                : `/${item.redireccionBoton}`;

                                            navigate(url, { replace: true });
                                        }
                                    }}
                                    sx={{
                                        padding: '16px',
                                        backgroundColor: '#F8F8F8',
                                        borderRadius: '8px',
                                        border: '1px solid #EEEEEE',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <ListItemText
                                        primary={item.tipoTitulo}
                                        sx={{
                                            '& .MuiTypography-root': {
                                                margin: 0,
                                                fontWeight: '700',
                                                fontSize: '23px',
                                                color: expandedItem === item.tipoTitulo ? '#EE2B7B' : '#303030',
                                            }
                                        }}
                                    />
                                    {hasSubMenu && (
                                        <Box sx={{
                                            color: '#EE2B7B',
                                            fontSize: '24px',
                                            fontWeight: 'bold',
                                            lineHeight: 1
                                        }}
                                            aria-hidden="true">
                                            {expandedItem === item.tipoTitulo ? '−' : '+'}
                                        </Box>
                                    )}
                                </ListItem>

                                {hasSubMenu && expandedItem === item.tipoTitulo && (
                                    <List
                                        role="menu"
                                        id={`submenu-${index}`}
                                        aria-label={`Submenú de ${item.tipoTitulo}`}
                                        sx={{
                                            backgroundColor: '#F8F8F8',
                                            border: '1px solid #EEEEEE',
                                            borderRadius: '0 0 8px 8px',            
                                            overflow: 'hidden',
                                            marginTop: '-25px',
                                            borderTop: 'none',
                                            position: 'relative',
                                            marginBottom: '20px',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: '5%',
                                                width: '89%',
                                                height: '2px',
                                                backgroundColor: '#E3E3E3',
                                            }
                                        }}>
                                        {item.referenciaContenido!.map((submenu, subIndex) => (
                                            <ListItem
                                                key={subIndex}
                                                component="button"
                                                role="menuitem"
                                                sx={{
                                                    padding: '16px',
                                                }}
                                                  onClick={e => {
                                                    e.preventDefault();
                                                    
                                                    const { href: currentHref } = window.location;
                                                    const currentUrl = new URL(currentHref);
                                                    const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
                                                    const baseSegment = pathSegments[0] || '';
                                                  
                                                    let redirectUrl = submenu.redireccionBoton.replace(/^\/+/, '/');
                                                  
                                                    const normalizePath = (...parts) =>
                                                      parts
                                                        .map(part => part.replace(/^\/+|\/+$/g, '')) // Remove leading/trailing slashes
                                                        .filter(Boolean)
                                                        .join('/');
                                                  
                                                    try {
                                                      // Si redirectUrl es una URL absoluta válida
                                                      new URL(redirectUrl);
                                                      window.location.href = redirectUrl;
                                                    } catch {
                                                      const origin = currentUrl.origin;
                                                  
                                                      let finalPath = '';
                                                      if (baseSegment && redirectUrl.includes(baseSegment)) {
                                                        const parts = redirectUrl.split(baseSegment);
                                                        const uniquePath = parts.pop(); 
                                                        finalPath = normalizePath(baseSegment, uniquePath);
                                                      } else {
                                                        finalPath = normalizePath(baseSegment, redirectUrl);
                                                      }
                                                  
                                                      window.location.href = `${origin}/${finalPath}`;
                                                    }
                                                  
                                                    handleDrawerClose();
                                                  }}
                                                  
                                            >
                                                <ListItemText
                                                    primary={submenu.tituloBanner}
                                                    sx={{
                                                        '& .MuiTypography-root': {
                                                            fontWeight: '700',
                                                            fontSize: '18px',
                                                            lineHeight: '23px',
                                                            color: '#303030; !important',
                                                        }
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </React.Fragment>
                        );
                    })}
                </List>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '20px'
                    }}
                >
                    {active === 'Empresas' && (
                        <Button
                            variant="outlined"
                            href={constructUrl(opciones[1].redireccionBoton)}
                            sx={{
                                color: '#EE2B7B',
                                borderColor: '#EE2B7B',
                                borderRadius: '20px',
                                textTransform: 'none',
                                padding: '6px 16px',
                                width: '92%',
                                height: '48px',
                                marginTop: '13px',
                            }}
                            aria-label={opciones[1].tituloBoton}
                        >
                            {opciones[1].tituloBoton}
                        </Button>
                    )}
                </Box>
                <Box
                    sx={{
                        padding: '24px 16px',
                    }}
                    role="region"
                    aria-label="Redes sociales"
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#303030',
                            marginBottom: '16px'
                        }}
                        id="social-media-heading"
                    >
                        Síguenos en redes sociales
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap'
                        }}
                        role="group"
                        aria-labelledby="social-media-heading"
                    >
                        {logosHeader.map((logo, index) => {
                            // Determine if this is the last social media icon
                            const isLastIcon = index === logosHeader.length - 1;

                            return (
                                <IconButton
                                    key={index}
                                    href={logo.description}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        padding: '8px',
                                        borderRadius: '8px',
                                        width: '50px',
                                        height: '50px',
                                    }}
                                    aria-label={`Visitar nuestra red social ${index + 1}`}
                                    onKeyDown={(e) => {
                                        // If this is the last icon and Tab is pressed, redirect focus to the menu button
                                        if (isLastIcon && e.key === 'Tab' && !e.shiftKey) {
                                            e.preventDefault();
                                            menuButtonRef.current?.focus();
                                        }
                                    }}
                                >
                                    <img
                                        src={`https:${logo.file.url}`}
                                        alt={`${logo.file.url} ${index + 1}`}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                        }}
                                    />
                                </IconButton>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default MobileMenu;
